import React from "react";
import { JobApplication } from "../../../Entities/JobApplication";
import { JobDescriptionEntity } from "../../../Entities/JobDescription";
import { Session } from "../../../Entities/Session";
import { useLoader } from "../../../Context/LoaderContext";
import interviewService from "../../../Service/interview.service";
import jobApplicationService from "../../../Service/job.application.service";
import emailService from "../../../Service/email.service";
import { message, Modal } from "antd";

interface UseApplicationStageChangeProps {
  jobApplication: JobApplication | null;
  setJobApplication: React.Dispatch<
    React.SetStateAction<JobApplication | null>
  >;
  jobPost: JobDescriptionEntity | null;
  interviewSessions: Session[];
  setInterviewSessions: React.Dispatch<React.SetStateAction<Session[]>>;
}

interface UseApplicationStageChangeReturn {
  handleMoveToAIAssessmentRound: () => Promise<void>;
  handleRestartAIAssessmentRound: () => Promise<void>;
  handleMarkAsShortlisted: () => Promise<void>;
  handleMarkAsRejected: () => Promise<void>;
  handleMarkAsHired: () => Promise<void>;
  handleAllowInterviewSchedule: () => Promise<void>;
  handleSendReminderNotification: (type: "profile.match" | "round") => Promise<void>;
}

const createVacancyLimitContent = (currentHired: number, openings: number) => {
  return React.createElement(
    "div",
    null,
    React.createElement(
      "p",
      null,
      "You have already hired ",
      React.createElement("strong", null, currentHired),
      " candidate(s) for this position."
    ),
    React.createElement(
      "p",
      null,
      "The job post has ",
      React.createElement("strong", null, openings),
      " opening(s)."
    ),
    React.createElement("p", null, "Do you want to proceed with hiring this candidate?")
  );
};

export const useApplicationStageChange = ({
  jobApplication,
  setJobApplication,
  jobPost,
  interviewSessions,
  setInterviewSessions,
}: UseApplicationStageChangeProps): UseApplicationStageChangeReturn => {
  const { setLoading, setLoadingMessage } = useLoader();

  const handleMoveToAIAssessmentRound = async () => {
    if (!jobApplication) {

      message.error("Unable to load candidate information. Please refresh and try again.");

      return;
    }
    try {
      setLoading(true);
      setLoadingMessage("Moving to AI assessment round...");

      const updatedApplication =
        await jobApplicationService.updateJobApplication(
          jobApplication._id,
          "applied",
        );

      if (updatedApplication) {
        setJobApplication(updatedApplication);

        // Refetch interview sessions as new sessions may be generated
        // Add retry logic as sessions might be created asynchronously on backend
        if (jobPost?.interviewRounds && jobPost.interviewRounds.length > 0) {
          setLoadingMessage("Loading interview sessions...");

          // Retry mechanism: try fetching sessions up to 5 times with exponential backoff
          // Sessions might be created asynchronously on the backend, so we need to poll
          const fetchSessionsWithRetry = async (
            retries = 5,
            initialDelay = 500,
          ) => {
            for (let i = 0; i < retries; i++) {
              try {
                const interviewSessionData =
                  await interviewService.getSessionByJobApplicationId(
                    jobApplication._id,
                  );

                // If sessions are found, update state and return
                if (interviewSessionData && interviewSessionData.length > 0) {
                  const sortedInterviewSessionData = interviewSessionData.sort(
                    (a: Session, b: Session) =>
                      new Date(b.updatedAt).getTime() -
                      new Date(a.updatedAt).getTime(),
                  );
                  setInterviewSessions(sortedInterviewSessionData);
                  return;
                }

                // If no sessions yet and not last retry, wait before retrying with exponential backoff
                if (i < retries - 1) {
                  const delay = initialDelay * Math.pow(2, i); // Exponential backoff: 500ms, 1s, 2s, 4s
                  await new Promise((resolve) => setTimeout(resolve, delay));
                }
              } catch (error) {
                console.error(
                  `Error fetching sessions (attempt ${i + 1}):`,
                  error,
                );
                // On last retry, still update with empty array to show proper state
                if (i === retries - 1) {
                  setInterviewSessions([]);
                  return;
                }
                // Wait before retrying on error too
                if (i < retries - 1) {
                  const delay = initialDelay * Math.pow(2, i);
                  await new Promise((resolve) => setTimeout(resolve, delay));
                }
              }
            }

            // If no sessions found after all retries, set empty array
            setInterviewSessions([]);
          };

          await fetchSessionsWithRetry();
        }
        message.success("Application moved to AI assessment round");
      } else {
        message.error("Failed to move to AI assessment round");
        return;
      }
    } catch (error: any) {
      console.error(
        "UseApplicationStageChange -> handleMoveToAIAssessmentRound -> Error:",
        error,
      );
      // openNotification(
      //   "error",
      //   "Error",
      //   "Failed to move to AI assessment round"
      // );
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const handleRestartAIAssessmentRound = async () => {
    try {
      const lastUnqualifiedSession = interviewSessions
        .filter((session) => session.status === "unqualified")
        .sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        )[0];

      if (!lastUnqualifiedSession) {
        message.error("The candidate hasn't been assessed yet or has already passed. Restart is only available for unsuccessful assessments.");
        return;
      }

      setLoading(true);
      setLoadingMessage("Restarting AI assessment round...");

      const response = await interviewService.restartInterviewSession(
        lastUnqualifiedSession._id,
      );

      if (response?.interviewSession && response?.jobApplication) {
        setJobApplication(response?.jobApplication);
        const newSessions = [
          ...interviewSessions,
          response?.interviewSession,
        ].sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        );
        setInterviewSessions(newSessions);
        message.success("AI assessment round restarted successfully");
      } else {
        message.error("Failed to restart AI assessment round");
        return;
      }
    } catch (error: any) {
      console.error(
        "UseApplicationStageChange -> handleRestartAIAssessmentRound -> Error:",
        error,
      );
      message.error("Failed to restart AI assessment round");
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const handleMarkAsShortlisted = async () => {
    if (!jobApplication) {
      message.error("Unable to load candidate information. Please refresh and try again.");
      return;
    }
    try {
      setLoading(true);
      setLoadingMessage("Marking as shortlisted...");

      const updatedApplication =
        await jobApplicationService.updateJobApplication(
          jobApplication._id,
          "shortlisted",
        );

      if (updatedApplication) {
        setJobApplication(updatedApplication);
        message.success("Application marked as shortlisted");
      } else {
        message.error("Failed to mark as shortlisted");
        return;
      }
    } catch (error: any) {
      console.error(
        "UseApplicationStageChange -> handleMarkAsShortlisted -> Error:",
        error,
      );
      message.error("Failed to mark as shortlisted");
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const handleMarkAsRejected = async () => {
    if (!jobApplication) {
      message.error("Unable to load candidate information. Please refresh and try again.");
      return;
    }
    try {
      setLoading(true);
      setLoadingMessage("Marking as rejected...");

      const updatedApplication =
        await jobApplicationService.updateJobApplication(
          jobApplication._id,
          "rejected",
        );

      if (updatedApplication) {
        setJobApplication(updatedApplication);
        message.success("Application marked as rejected");
      } else {
        message.error("Failed to mark as rejected");
        return;
      }
    } catch (error: any) {
      console.error(
        "UseApplicationStageChange -> handleMarkAsRejected -> Error:",
        error,
      );
      message.error("Failed to mark as rejected");
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const handleMarkAsHired = async () => {
    if (!jobApplication) {
      message.error("Unable to load candidate information. Please refresh and try again.");
      return;
    }

    // Check vacancy limit
    if (jobPost?.numberOfOpenings && jobPost?.numberOfHiredApplications !== undefined) {
      const currentHired = jobPost.numberOfHiredApplications;
      const openings = jobPost.numberOfOpenings;

      if (currentHired >= openings) {
        Modal.confirm({
          title: "Vacancy Limit Reached",
          content: createVacancyLimitContent(currentHired, openings),
          okText: "Yes, Hire",
          cancelText: "Cancel",
          onOk: async () => {
            await performHiring();
          },
        });
        return;
      }
    }

    await performHiring();
  };

  const performHiring = async () => {
    if (!jobApplication) {
      message.error("Unable to load candidate information. Please refresh and try again.");
      return;
    }

    try {
      setLoading(true);
      setLoadingMessage("Marking as hired...");

      const updatedApplication =
        await jobApplicationService.updateJobApplication(
          jobApplication._id,
          "hired",
        );
      if (updatedApplication) {
        setJobApplication(updatedApplication);
        message.success("Application marked as hired");
      } else {
        message.error("Failed to mark as hired");
        return;
      }
    } catch (error: any) {
      console.error(
        "UseApplicationStageChange -> handleMarkAsHired -> Error:",
        error,
      );
      message.error("Failed to mark as hired");
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const handleAllowInterviewSchedule = async () => {
    if (!jobApplication) {
      message.error("Unable to load candidate information. Please refresh and try again.");
      return;
    }
    try {
      setLoading(true);
      setLoadingMessage("Allowing interview schedule...");

      const updatedApplication =
        await jobApplicationService.updateJobApplication(
          jobApplication._id,
          "interview",
        );
      if (updatedApplication) {
        setJobApplication(updatedApplication);

        // Refetch interview sessions as status change might affect sessions
        if (jobPost?.interviewRounds && jobPost.interviewRounds.length > 0) {
          setLoadingMessage("Loading interview sessions...");
          const interviewSessionData =
            await interviewService.getSessionByJobApplicationId(
              jobApplication._id,
            );
          const sortedInterviewSessionData = interviewSessionData.sort(
            (a: Session, b: Session) =>
              new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
          );
          setInterviewSessions(sortedInterviewSessionData);
        }
        message.success("Interview schedule allowed");

      } else {
        message.error("Failed to allow interview schedule");
        return;
      }
    } catch (error: any) {
      console.error(
        "UseApplicationStageChange -> handleAllowInterviewSchedule -> Error:",
        error,
      );
      message.error("Failed to allow interview schedule");
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const handleSendReminderNotification = async (type: "profile.match" | "round") => {
    if (!jobApplication) {
      message.error("Unable to load candidate information. Please refresh and try again.");
      return;
    }
    try {
      setLoading(true);
      setLoadingMessage(
        type === "profile.match"
          ? "Sending resume upload reminder email..."
          : "Sending round completion reminder email...",
      );
      if (type === "profile.match") {
        await emailService.sendResumeUploadReminder(
          jobApplication._id,
          jobApplication.orgId,
        );
        message.success("Resume upload reminder email sent");
      } else {
        const lastPendingSession = interviewSessions
          .filter((session) => session.status === "pending")
          .sort(
            (a, b) =>
              new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
          )[0];
        if (!lastPendingSession) {
          message.error("No active interview round found.");
          return;
        }
        await emailService.sendInterviewRoundReminder(
          jobApplication._id,
          jobApplication.orgId,
        );
        message.success("Round completion reminder email sent");
      }
    } catch (error: any) {
      console.error(
        "UseApplicationStageChange -> handleSendEmail -> Error:",
        error,
      );
      message.error("Failed to send email");
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  return {
    handleMoveToAIAssessmentRound,
    handleRestartAIAssessmentRound,
    handleMarkAsShortlisted,
    handleMarkAsRejected,
    handleMarkAsHired,
    handleAllowInterviewSchedule,
    handleSendReminderNotification,
  };
};
